import { toPng } from 'html-to-image';
import { type ExportData } from '#src/utils/export-data';
import { translate } from './i18n/translate';

/**
 * Builds the subtitle text from the export data
 */
function buildSubtitle(data: ExportData): string {
  return `Show: ${data.dataset}; At: ${data.levelDetailed}; In: ${data.year}; By: ${data.categoryDetailed}`.trim();
}

/**
 * Generates a filename from the export data
 */
function generateFilename(data: ExportData): string {
  const parts = ['gfx', data.dataset, data.level, data.year, data.category]
    .filter(Boolean)
    .map((part) => part.toLowerCase().replace(/\s+/g, '-'));

  return `${parts.join('_')}.png`;
}

/**
 * Creates a header element with title, subtitle, and logo
 */
function createHeader(subtitle: string, width: number): HTMLDivElement {
  const isMobile = window.innerWidth < 768; // md breakpoint

  // Create header container
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.width = `${width}px`;

  if (isMobile) {
    // Mobile: vertical layout with smaller sizes
    header.style.flexDirection = 'column';
    header.style.alignItems = 'flex-start';
    header.style.gap = '6px';
    header.style.marginBottom = '16px';

    // Logo on first row
    const logoImg = document.createElement('img');
    logoImg.src = '/themes/custom/project/logo.svg';
    logoImg.style.height = '32px';
    logoImg.style.width = 'auto';

    // Title on second row
    const title = document.createElement('h1');
    title.textContent = translate('gfx.download.title');
    title.style.fontSize = '16px';
    title.style.fontWeight = 'bold';
    title.style.margin = '0';

    // Subtitle on third row
    const subtitleEl = document.createElement('p');
    subtitleEl.textContent = subtitle;
    subtitleEl.style.fontSize = '10px';
    subtitleEl.style.fontWeight = 'bold';
    subtitleEl.style.margin = '0';
    subtitleEl.style.color = '#000';

    header.appendChild(logoImg);
    header.appendChild(title);
    header.appendChild(subtitleEl);
  } else {
    // Desktop: horizontal layout with original sizes
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'flex-start';
    header.style.marginBottom = '20px';

    // Create title and subtitle container
    const leftContainer = document.createElement('div');
    leftContainer.style.display = 'flex';
    leftContainer.style.flexDirection = 'column';
    leftContainer.style.justifyContent = 'space-between';
    leftContainer.style.flex = '1';
    leftContainer.style.height = '60px';

    const title = document.createElement('h1');
    title.textContent = translate('gfx.download.title');
    title.style.fontSize = '36px';
    title.style.fontWeight = 'bold';
    title.style.margin = '0';

    const subtitleEl = document.createElement('p');
    subtitleEl.textContent = subtitle;
    subtitleEl.style.fontSize = '12px';
    subtitleEl.style.fontWeight = 'bold';
    subtitleEl.style.margin = '0';
    subtitleEl.style.color = '#000';

    leftContainer.appendChild(title);
    leftContainer.appendChild(subtitleEl);

    // Load and add logo
    const logoImg = document.createElement('img');
    logoImg.src = '/themes/custom/project/logo.svg';
    logoImg.style.height = '60px';
    logoImg.style.width = 'auto';
    logoImg.style.flexShrink = '0';
    logoImg.style.marginLeft = '20px';

    header.appendChild(leftContainer);
    header.appendChild(logoImg);
  }

  return header;
}

/**
 * Common helper to prepare year stepper for export
 */
function prepareYearStepper(contentClone: HTMLElement): void {
  const yearStepperClone = contentClone.querySelector(
    '.years-stepper-items'
  ) as HTMLElement;
  if (!yearStepperClone) return;

  const allYears = Array.from(
    yearStepperClone.querySelectorAll('.years-stepper-item')
  ) as HTMLElement[];
  const activeYearClone = allYears.find((year) =>
    year.classList.contains('active')
  );

  if (activeYearClone && allYears.length > 8) {
    const activeIndex = allYears.indexOf(activeYearClone);

    // If active year is beyond the first 8 visible items, remove years from the start
    if (activeIndex >= 8) {
      const yearsToRemove = activeIndex - 7; // Keep active year around position 7
      for (let i = 0; i < yearsToRemove; i++) {
        allYears[i].remove();
      }
    }
  }

  // Hide scrollbar
  yearStepperClone.style.overflow = 'hidden';
}

/**
 * Common helper to generate and download PNG
 */
async function generateAndDownloadPng(
  wrapper: HTMLElement,
  header: HTMLElement,
  fileName?: string
): Promise<void> {
  try {
    // Wait for logo to load
    const logoImg = header.querySelector('img');
    if (logoImg) {
      await new Promise((resolve) => {
        if (logoImg.complete) {
          resolve(null);
        } else {
          logoImg.onload = () => resolve(null);
          logoImg.onerror = () => resolve(null);
        }
      });
    }

    // Generate PNG
    const png = await toPng(wrapper, {
      backgroundColor: '#ffffff',
      height: wrapper.scrollHeight,
      width: wrapper.scrollWidth,
      skipFonts: true, // Skip font embedding to avoid Firefox issues
      pixelRatio: 2, // Higher quality export
    });

    // Download
    const link = document.createElement('a');
    link.setAttribute('rel', 'noopener noreferrer');
    link.setAttribute('href', png);
    link.setAttribute(
      'download',
      fileName || `gfx-${new Date().toISOString()}.png`
    );
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
  } catch (error) {
    console.error('Failed to generate PNG:', error);
    alert('Failed to download image. Please try again.');
  } finally {
    // Always cleanup wrapper
    if (document.body.contains(wrapper)) {
      document.body.removeChild(wrapper);
    }
    wrapper.remove();
  }
}

/**
 * Exports chart view as PNG with header (title, logo, subtitle)
 */
export async function exportChartToPng(exportData: ExportData): Promise<void> {
  const container = document.querySelector('.chart-layout-main');
  if (!container) {
    console.error('Chart container not found');
    return;
  }

  const subtitle = buildSubtitle(exportData);
  const fileName = generateFilename(exportData);
  const contentClone = container.cloneNode(true) as HTMLElement;
  const contentWidth = container.scrollWidth;
  contentClone.style.width = `${contentWidth}px`;

  // Create wrapper container
  const wrapper = document.createElement('div');
  wrapper.style.backgroundColor = '#ffffff';
  wrapper.style.padding = '40px';
  wrapper.style.width = `${contentWidth + 80}px`;
  wrapper.style.fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // Create and add header
  const header = createHeader(subtitle, contentWidth);

  // Assemble wrapper
  wrapper.appendChild(header);
  wrapper.appendChild(contentClone);

  // Add to document temporarily
  document.body.appendChild(wrapper);

  // Remove scroll container - expand to full content height
  const scrollContainer = contentClone.querySelector(
    '.chart-scroll-container'
  ) as HTMLElement;
  if (scrollContainer) {
    const fullHeight = scrollContainer.scrollHeight;
    scrollContainer.style.height = `${fullHeight}px`;
    scrollContainer.style.maxHeight = 'none';
    scrollContainer.style.overflow = 'visible';
  }

  // Ensure parent container doesn't clip the expanded content
  contentClone.style.height = 'auto';
  contentClone.style.minHeight = 'auto';
  contentClone.style.maxHeight = 'none';

  // Prepare year stepper
  prepareYearStepper(contentClone);

  // Generate and download
  await generateAndDownloadPng(wrapper, header, fileName);
}

/**
 * Exports map view as PNG with header (title, logo, subtitle)
 */
export async function exportMapToPng(exportData: ExportData): Promise<void> {
  const container = document.querySelector('.map-layout-main');
  if (!container) {
    console.error('Map container not found');
    return;
  }

  const subtitle = buildSubtitle(exportData);
  const fileName = generateFilename(exportData);
  const contentClone = container.cloneNode(true) as HTMLElement;
  const contentWidth = container.scrollWidth;
  contentClone.style.width = `${contentWidth}px`;

  // Create wrapper container
  const wrapper = document.createElement('div');
  wrapper.style.backgroundColor = '#ffffff';
  wrapper.style.padding = '40px';
  wrapper.style.width = `${contentWidth + 80}px`;
  wrapper.style.fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // Create and add header
  const header = createHeader(subtitle, contentWidth);

  // Assemble wrapper
  wrapper.appendChild(header);
  wrapper.appendChild(contentClone);

  // Add to document temporarily
  document.body.appendChild(wrapper);

  // Hide mobile legend (it's for mobile view only, not for export)
  const mobileLegend = contentClone.querySelector(
    '.map-mobile-legend-container'
  ) as HTMLElement;
  if (mobileLegend) {
    mobileLegend.style.display = 'none';
  }

  // Prepare year stepper
  prepareYearStepper(contentClone);

  // Generate and download
  await generateAndDownloadPng(wrapper, header, fileName);
}
